import { AxiosError } from "axios";
import { useMemo } from "react";
import { TemplateId } from "src/globalTypes";
import { FormatMsg } from "src/hooks/useFormatMsg";
import { getFile } from "src/requests/files/requestCreators";
import { FileMetadata } from "src/requests/files/types";
import { RowKey, TableRow } from "src/requests/table/types";
import { base64ToArrayBuffer } from "src/utils/dataParsing";
import getShortUuid from "src/utils/nanoid";
import { v4 } from "uuid";
import { GetFileParams, RawFile } from "../types";

type ButtonIdCreator = (
  templateId: TemplateId | null,
  rowIdentifier: string
) => string;

export const createSaveButtonId: ButtonIdCreator = (
  templateId,
  rowIdentifier
) => `accept_${String(templateId)}_${rowIdentifier}`;

export const createDeclineButtonId: ButtonIdCreator = (
  templateId,
  rowIdentifier
) => `decline_${String(templateId)}_${rowIdentifier}`;

export const createRawFile = (file: File): RawFile => ({
  id: `${v4()}_raw`,
  file,
});

export const isFileRaw = (fileId: string | number): boolean =>
  !!String(fileId).match(/_raw$/);

export const rawFileToDefaultFileMetadata = (
  file: RawFile,
  fileId?: string
): FileMetadata => ({
  id: fileId || file.id,
  fileName: file.file.name,
  contentType: file.file.type,
});

export const createFileMetadataFromResponse = (
  fileId: string,
  rawFileId: string,
  rawFiles: RawFile[]
): FileMetadata => {
  const rawFile = rawFiles.find((file) => file.id === rawFileId);

  // tohle nikdy nenastane, je to kvuli ochcani TS, v poli rawFiles
  // vzdy bude file s danym id
  if (!rawFile) return {} as FileMetadata;

  return rawFileToDefaultFileMetadata(rawFile, fileId);
};

type RawFilesAndFilesMetadata = {
  rawFiles: RawFile[];
  filesMetadata: FileMetadata[];
};
export const getRawFilesAndFilesMetadataFromFileList = (
  files: FileList
): RawFilesAndFilesMetadata => {
  // have to convert FileList to File[], FileList is not array but object
  const filesArr = Array.from(files);
  const rawFiles = filesArr.map(createRawFile);
  const filesMetadata = rawFiles.map((rawFile) =>
    rawFileToDefaultFileMetadata(rawFile)
  );
  return { rawFiles, filesMetadata };
};

export const getBlob = async (params: GetFileParams): Promise<Blob> => {
  try {
    const response = await getFile({
      fileId: params.file.id,
      templateId: params.templateId,
      templateItemId: params.templateItemId,
    });

    const arrayBufferView = base64ToArrayBuffer(response.data);
    return new Blob([arrayBufferView], { type: params.file.contentType });
  } catch (e) {
    throw new Error((e as AxiosError).response?.data ?? "");
  }
};

export const getRecordSubjectNumber = (
  row: TableRow | undefined,
  hegSubjectNumberColumnName: string | null
): number | string => {
  if (!row?.cells || hegSubjectNumberColumnName == null) return 0;

  const tableCell = Object.values(row.cells).find(
    (x) => x.columnName === `${row.tableName}.${hegSubjectNumberColumnName}`
  );

  if (tableCell) return String(tableCell.value);

  return 0;
};

export const getErrorMsgForUploadFailedFiles = (
  failedFileIds: string[],
  addedFiles: RawFile[]
): string | null => {
  if (!addedFiles.length) return null;

  const failedFiles = addedFiles.filter((file) =>
    failedFileIds.includes(file.id)
  );

  return failedFiles.map((file) => file.file.name).join(", ");
};

export const getErrorMsgForDeleteFailedFiles = (
  failedFileIds: string[],
  deletedFiles: FileMetadata[]
): string | null => {
  if (!deletedFiles.length) return null;

  const failedFiles = deletedFiles.filter((file) =>
    failedFileIds.includes(String(file.id))
  );

  return failedFiles.map((file) => file.fileName).join(", ");
};

export const getMultipleFilesBtnMsg = (
  filesLength: number,
  formatMsg: FormatMsg
): string =>
  formatMsg("segments.file_list.multiple_files", { count: filesLength });

export const getPopoverId = (popoverOpen: boolean): string | undefined =>
  popoverOpen ? "file-list-menu" : undefined;

export const canBeFileOpenedInBrowser = (fileName: string): boolean => {
  const allowedFilesToOpenInBrowser = [
    "jpg",
    "jpeg",
    "png",
    "txt",
    "pdf",
    "gif",
    "webp",
  ];
  const splittedFileName = fileName.split(".");
  const extension = splittedFileName[splittedFileName.length - 1];

  if (!extension) return false;

  return allowedFilesToOpenInBrowser.includes(extension.toLowerCase());
};

export const getUploadActionIdForRow = (
  templateId: TemplateId,
  rowKey: RowKey,
  columnId: string
): string => `${templateId}-${rowKey}-${columnId}-upload`;

export const getDeleteActionIdForRow = (
  templateId: TemplateId,
  rowKey: RowKey,
  columnId: string
): string => `${templateId}-${rowKey}-${columnId}-delete`;

export const useUniqueIdentifier = (): string =>
  useMemo(() => getShortUuid(), []);

export const getFilesCount = (
  filesCount: number,
  files: FileMetadata[],
  editMode: boolean
) => {
  if (!editMode) return filesCount;

  return files.length;
};
